import { Button, Alert, Loading } from 'framework/ui/mui-builder';
import React, { useState, useEffect, useCallback, useMemo } from 'react';

import { TimelineList, TimelineRecordWrapper } from 'framework/ui/timeline';
import useEmployeeFeedbacksService from 'domain/people/employee/feedbacks/EmployeeFeedbacksService';
import useFeedbackAboutMeRegistry from 'domain/people/employee/feedbacks/FeedbackAboutMeRegistry';
import { Box } from '@mui/material';
import AboutMeFeedbackModal from '../Timeline/AboutMeFeedbackModal';

const AboutMeFeedbackPreview: React.FC = () => {
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalElements, setTotalElements] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const { search } = useEmployeeFeedbacksService();
  const feedbackRegistry = useFeedbackAboutMeRegistry();

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await search({
        pageSize: 3,
        pageNumber: 0,
      });
      setItems(response?.result || []);
      setTotalElements(response?.totalElements || 0);
    } catch (error) {
      setItems([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleOpenModal = () => {
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const sortedItems = useMemo(() => {
    return items.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [items]);

  const renderFeedbackList = () => {
    if (loading) {
      return <Loading />;
    }

    if (!items.length) {
      return (
        <Alert severity="info" sx={{ mt: 1.5 }}>
          No feedback about my work yet.
        </Alert>
      );
    }

    return (
      <TimelineList>
        {sortedItems.map((item: any) => {
          if (!feedbackRegistry.isApplicable(item)) return null;

          const { source, RecordLayout } = feedbackRegistry;
          const timelineItemEntity = item && item[source] ? item[source] : item;

          if (!timelineItemEntity) return null;

          return (
            <TimelineRecordWrapper entity={timelineItemEntity} key={`wrapper-${timelineItemEntity.id}`}>
              <RecordLayout isHighlighted={false} isEditing={false} key={`record-${timelineItemEntity.id}`} />
            </TimelineRecordWrapper>
          );
        })}
      </TimelineList>
    );
  };

  return (
    <>
      {renderFeedbackList()}
      {totalElements > 3 && (
        <Box display="flex" flexDirection="column" justifyContent="flex-end">
          <Button onClick={handleOpenModal} variant="outlined" size="small" sx={{ alignSelf: 'flex-end' }}>
            View all feedback about me
          </Button>
        </Box>
      )}
      <AboutMeFeedbackModal open={modalOpen} onClose={handleCloseModal} />
    </>
  );
};

export default AboutMeFeedbackPreview;
